// framework imports - 1st party
import React, { useState, useEffect } from "react";

// lib imports - 3rd party
import "airbnb-js-shims"; // needed for IE11 support
import { ToastContainer, Slide } from "react-toastify";

// app imports
import SummaryTable from "./Summary/SummaryTable";
import Spinner from "./HelperComponents/Spinner";
import Reason from "./HelperComponents/Reason";
import * as Auth from "../authService";
import * as notify from "../notify";
import * as api from "../api";

import mainLogo from "../icons/summary.png";
import "../stylesheets/spinner.css";
import "../stylesheets/PageBody.css";
import withRouter from "./HelperComponents/WithRouter";
import {
  isEscheatedTransaction,
  normalizeStatus,
} from "./Summary/transactionCheckboxState";

//The Transaction Summary page
const TransactionSummary = (props) => {
  //Fields to make the call to update Notify table
  let transId = "";
  let type = "";
  let destination = "";
  let attention = "";
  let transactionIdsList = "";

  const [loading, setLoading] = useState(false);
  const [rowsChecked, setRowsChecked] = useState([]);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [canUndoApproval, setUndoApproval] = useState(false);
  const [canReject, setCanReject] = useState(false);
  const [topChecked, setTopChecked] = useState(false); // whether top checkmark is checked
  const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // "undo" | "reject"
  // check if state
  // has parameters
  // that require notification to show up top
  useEffect(() => {
    notify.check(props.location.state);
    props.navigate(".", { state: {} });
  }, []);

  //Get NotifyInfo for all the transactions that were successfully approved
  const getTransactionsNotifyInfo = () => {
    api.getNotifyInfoListTransactions(transactionIdsList).then((x) => {
      // successful
      if (x.status === 200 || x.status === 201) {
        //Loop thru each transactio Id to get Notify Type Info
        for (let i = 0; i < x.data.length; i++) {
          resetSendNotificationValues();
          const wireEntry = x.data[i].notifyType.find(
            (item) => item.value === "wir"
          );
          if (wireEntry) {
            transId = x.data[i].transactionId;
            type = wireEntry.value;
            destination = wireEntry.notifyDest;
            attention = wireEntry.notifyAttn;
            handleWireNotify();
          }
        }
      } else {
        notify.error("Error in Retreiving Notify Information Service");
      }
    });
  };
  //Log approved wire transactions into Notify table
  const handleWireNotify = () => {
    api.sendNotification(transId, type, destination, attention).then((x) => {
      if (x.status === 200 || x.status === 201) {
        resetSendNotificationValues();
      } else {
        notify.error("Sending Notification Failed for reference #" + transId);
        resetSendNotificationValues();
      }
    });
  };
  //Resetting values for next Notification Call to Notify table
  const resetSendNotificationValues = () => {
    console.log("Resetting values");
    transId = "";
    type = "";
    destination = "";
    attention = "";
  };
  const restoreBulkActionButtons = () => {
    setCanApprove(hasSelectedReadyTransactions());
    setUndoApproval(hasSelectedApprovedTransactions());
    setCanReject(canRejectSelection());
  };

  //Approve all checked rows
  const handleApprove = () => {
    const approvedIds = selectedTransactions
      .filter(
        (transaction) =>
          normalizeStatus(transaction.confirmationStatus) === "READY"
      )
      .map((transaction) => transaction.transactionId);
    if (approvedIds.length === 0) {
      return;
    }
    // disable all buttons immediately
    setCanApprove(false);
    setUndoApproval(false);
    setCanReject(false);

    api
      .approveTransactions(approvedIds)
      .then((x) => {
        // successful
        if (x.status === 200 || x.status === 201) {
          // customize display message based on count of rows checked
          approvedIds.length > 1
            ? notify.success("Approved " + approvedIds.length + " transactions")
            : notify.success("Approved transaction " + approvedIds[0]);
          console.log("Getting Notify Info and Login wire transactions");
          //Setting Transaction list to get list of NotifyInfo
          transactionIdsList = approvedIds;
          //Call to get NotifyInfo of all approved transactions.
          getTransactionsNotifyInfo();
          setSummaryRefreshKey((key) => key + 1);
        } else {
          notify.error("Failed to approve transaction(s)");
          restoreBulkActionButtons();
        }
      })
      .catch(() => {
        notify.error("Failed to approve transaction(s)");
        restoreBulkActionButtons();
      });
  };

  const handleUndoApproval = () => {
    setPendingAction("undo");
    setShowReasonModal(true);
  };

  const handleReject = () => {
    setPendingAction("reject");
    setShowReasonModal(true);
  };

  const handleReasonConfirm = (reason) => {
    setShowReasonModal(false);
    // disable all buttons when confirm is clicked (API call in progress)
    setCanApprove(false);
    setUndoApproval(false);
    setCanReject(false);

    const actionIds = [...rowsChecked];
    const approvedIds = getActionableSelectedTransactions()
      .filter(
        (transaction) =>
          normalizeStatus(transaction.confirmationStatus) === "APPROVED"
      )
      .map((transaction) => transaction.transactionId);

    if (pendingAction === "undo") {
      api
        .undoApprovalTransactions(approvedIds, reason)
        .then((x) => {
          // successful
          if (x.status === 200 || x.status === 201) {
            const successCount = x.data.successCount || 0;
            const failureCount = x.data.failureCount || 0;
            const failedTransactionIds = x.data.transactionIds || [];

            // Show success notification if there are successful transactions
            if (successCount > 0) {
              notify.success(
                "Undo approval for " + successCount + " transactions",
                true
              );
            }

            // Show failure notification if there are failed transactions
            // delay slightly so both toasts are visible at the same time
            if (failureCount > 0) {
              const failureMsg =
                failureCount > 1
                  ? `Failed to undo approval for ${failureCount} transactions: ${failedTransactionIds.join(
                      ", "
                    )}`
                  : `Failed to undo approval for transaction: ${failedTransactionIds.join(
                      ", "
                    )}`;
              setTimeout(
                () => notify.error(failureMsg, true),
                successCount > 0 ? 500 : 0
              );
            }

            if (successCount > 0) {
              const failedIdSet = new Set(
                failedTransactionIds.map((id) => String(id))
              );
              transactionIdsList = approvedIds.filter(
                (id) => !failedIdSet.has(String(id))
              );
              getTransactionsNotifyInfo();
            }

            setSummaryRefreshKey((key) => key + 1);
          } else {
            notify.error("Failed to undo approval for transaction(s)");
            restoreBulkActionButtons();
          }
        })
        .catch(() => {
          notify.error("Failed to undo approval for transaction(s)");
          restoreBulkActionButtons();
        });
    } else if (pendingAction === "reject") {
      api
        .bulkDenyTransactions(actionIds, reason)
        .then((x) => {
          // successful
          if (x.status === 200 || x.status === 201) {
            const successCount = x.data.successCount || 0;
            const failureCount = x.data.failureCount || 0;
            const failedTransactionIds = x.data.transactionIds || [];

            // Show success notification if there are successful transactions
            if (successCount > 0) {
              notify.success(
                "Rejected " + successCount + " transactions",
                true
              );
            }

            // Show failure notification if there are failed transactions
            // delay slightly so both toasts are visible at the same time
            if (failureCount > 0) {
              const failureMsg =
                failureCount > 1
                  ? `Failed to reject ${failureCount} transactions: ${failedTransactionIds.join(
                      ", "
                    )}`
                  : `Failed to reject transaction: ${failedTransactionIds.join(
                      ", "
                    )}`;
              setTimeout(
                () => notify.error(failureMsg, true),
                successCount > 0 ? 500 : 0
              );
            }

            transactionIdsList = actionIds;
            getTransactionsNotifyInfo();
            setSummaryRefreshKey((key) => key + 1);
          } else {
            notify.error("Failed to reject transaction(s)");
            restoreBulkActionButtons();
          }
        })
        .catch(() => {
          notify.error("Failed to reject transaction(s)");
          restoreBulkActionButtons();
        });
    }

    setPendingAction(null);
  };

  const handleReasonCancel = () => {
    setShowReasonModal(false);
    setPendingAction(null);
    // re-enable buttons based on current selection
    setCanApprove(hasSelectedReadyTransactions());
    setUndoApproval(hasSelectedApprovedTransactions());
    setCanReject(canRejectSelection());
  };

  const getActionableSelectedTransactions = (
    transactions = selectedTransactions
  ) =>
    transactions.filter(
      (transaction) =>
        !isEscheatedTransaction(transaction) &&
        ["READY", "APPROVED"].includes(
          (transaction.confirmationStatus || "").toUpperCase()
        )
    );

  const hasSelectedReadyTransactions = (transactions = selectedTransactions) =>
    getActionableSelectedTransactions(transactions).some(
      (transaction) =>
        normalizeStatus(transaction.confirmationStatus) === "READY"
    );

  const hasSelectedApprovedTransactions = (
    transactions = selectedTransactions
  ) =>
    getActionableSelectedTransactions(transactions).some(
      (transaction) =>
        normalizeStatus(transaction.confirmationStatus) === "APPROVED"
    );

  const canRejectSelection = (transactions = selectedTransactions) =>
    getActionableSelectedTransactions(transactions).length > 0;

  const getReasonModalMessage = () => {
    const undoCount = getActionableSelectedTransactions().filter(
      (transaction) =>
        normalizeStatus(transaction.confirmationStatus) === "APPROVED"
    ).length;
    const transactionCount =
      pendingAction === "undo" ? undoCount : rowsChecked.length;
    const transactionLabel =
      transactionCount === 1 ? "transaction" : "transactions";

    if (pendingAction === "undo") {
      return `Do you want to undo approval for these ${transactionCount} ${transactionLabel}? Undoing approval will move the selected transactions back out of approved status.`;
    }

    return `Do you want to reject these ${transactionCount} ${transactionLabel}? Rejecting refunds the full amount to payer and cannot be undone. Click cancel to return to the Transaction Summary if you need to review the selected transactions first.`;
  };

  // if a row is checked, this function gets called
  const handleRowsChecked = (e, selectedRows = []) => {
    // Approve when the selection includes Ready; undo when it includes Approved
    setCanApprove(hasSelectedReadyTransactions(selectedRows));
    setUndoApproval(hasSelectedApprovedTransactions(selectedRows));
    // Reject when the selection has Ready and/or Approved rows, including mixed
    setCanReject(canRejectSelection(selectedRows));
    // set correct rows to be checked
    setRowsChecked(e);
    setSelectedTransactions(selectedRows);
  };

  return (
    <main className="main-background">
      <div className="main-component">
        <div id="table-header">
          <div id="details-label">
            <img alt="Govpay Logo" src={mainLogo} id="trans-sum-icon" />
            <div id="details-label-name">Transaction Summary</div>
          </div>
          {Auth.canAdmin() ||
          Auth.canFinanceOverride() ||
          Auth.canAmountModify() ||
          Auth.canModify() ? (
            <button
              className="proview-button right margins hide-mobile"
              disabled={!canApprove}
              onClick={handleApprove}
            >
              Approve
            </button>
          ) : null}
          {Auth.canEditApprovedSummaryCheckbox() ? (
            <button
              className="proview-button right margins hide-mobile"
              disabled={!canUndoApproval}
              onClick={handleUndoApproval}
            >
              Undo Approval
            </button>
          ) : null}
          {Auth.canAdmin() || Auth.canFinanceOverride() ? (
            <button
              className="proview-button right margins hide-mobile"
              disabled={!canReject}
              onClick={handleReject}
            >
              Reject
            </button>
          ) : null}
          {/* Spinner shown only when table is loading */}
          <Spinner customClass={"sk-fading-circle summary"} loading={loading} />
        </div>
        {/* All table information in Table component */}
        <SummaryTable
          setSpinner={(e) => setLoading(e)}
          loading={loading}
          handleRowsChecked={(e, selectedRows) =>
            handleRowsChecked(e, selectedRows)
          }
          rowsChecked={rowsChecked}
          setTopChecked={(e) => setTopChecked(e)}
          topChecked={topChecked}
          summaryRefreshKey={summaryRefreshKey}
        />
      </div>
      <Reason
        show={showReasonModal}
        title={
          pendingAction === "undo" ? "Undo Approval Reason" : "Rejection Reason"
        }
        submitLabel={pendingAction === "undo" ? "Undo Approval" : "Reject"}
        message={getReasonModalMessage()}
        onConfirm={handleReasonConfirm}
        onCancel={handleReasonCancel}
      />
      <ToastContainer transition={Slide} />
    </main>
  );
};

export default withRouter(TransactionSummary);
